/**
 * 認証済みユーザーのアクセスを制限するルート（ログインページなど）
 */
import React, { useMemo } from 'react'; 
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * パブリック専用ルート（認証済みユーザーはアクセス不可）
 * @param {Object} props - プロパティ
 * @param {React.ReactNode} props.children - 保護対象のコンポーネント
 * @param {string} props.redirectTo - リダイレクト先（デフォルト: /home）
 * @returns {React.ReactNode} 未認証の場合は子コンポーネント、認証済みの場合はリダイレクト
 */
const PublicOnlyRoute = ({ 
  children, 
  redirectTo = '/home'
}) => {
  const { loading, isAuthenticated } = useAuth();
  const location = useLocation();

  /**
   * 登録フロー中かどうかを判定する関数
   */
  const isRegistrationFlowPath = useMemo(() => {
    // 登録フロー関連のパス
    const registrationPaths = [
      '/terms', 
      '/auth', 
      '/register', 
      '/credit-card-input', 
      '/registration-complete'
    ];

    return registrationPaths.includes(location.pathname);
  }, [location.pathname]);

  // 認証状態のロード中は専用画面を表示
  if (loading) {
    return (
      <div style={loadingStyles.container}>
        <div style={loadingStyles.spinner}></div>
        <p style={loadingStyles.text}>認証状態を確認中...</p>
      </div>
    );
  }

  // 登録フロー中は認証済みでもリダイレクトしない
  if (isRegistrationFlowPath) {
    return children;
  }

  // 通常のログイン後リダイレクト
  if (isAuthenticated()) {
    return <Navigate to={redirectTo} replace />;
  }

  // 未認証の場合は子コンポーネントを表示
  return children;
};

/**
 * ローディング画面のスタイル
 */
const loadingStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#F0F8FF'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  text: {
    color: '#333',
    fontSize: '16px',
    fontWeight: '500'
  }
};

// CSS アニメーション（インラインで追加）
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// スタイルタグをヘッドに追加（一度だけ）
if (typeof document !== 'undefined' && !document.querySelector('#public-only-route-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'public-only-route-styles';
  styleSheet.textContent = spinKeyframes;
  document.head.appendChild(styleSheet);
}

export default PublicOnlyRoute;